// Pure helpers for the Assistant settings page (spec: duty-assistant-intake-ux, w-admin).
import { labelFor } from '../../../utils/wireKeys'

// The eight switches, in the order the page shows them, with what the customer sees either way
// (w-admin Req 2.1).
export const SERVICES_GROUP = 'Services on WhatsApp'

export const SWITCHES = [
  {
    key: 'adWelcomeEnabled',
    label: 'Ad welcome',
    help:
      'On: people arriving from the WhatsApp ad get a welcome that explains the service, the price and how to start. Off: the old welcome.'
  },
  {
    key: 'adFaqEnabled',
    label: 'Ad FAQ answers',
    help:
      'On: the ad\'s question buttons get instant answers. Off: they are answered by the assistant\'s AI.'
  },
  {
    key: 'adNudgeEnabled',
    label: 'Ad follow-up',
    help:
      'On: one free reminder an hour after the welcome to anyone from the ad who hasn\'t replied. Off: no reminder.'
  },
  {
    key: 'numberedChoicesEnabled',
    label: 'Numbered choices',
    help:
      'On: every list also shows numbered options people can reply to with a number. Off: tap-only lists.'
  },
  {
    key: 'entryChecksEnabled',
    label: 'Check answers as typed',
    help:
      'On: an engine size or fuel type the car never came with is questioned straight away. Off: only the final check catches it.'
  },
  {
    key: 'imageReadingEnabled',
    label: 'Read pictures (pilot)',
    help:
      'On: pictures of a listing, document or chassis number sent early in a chat are read and confirmed with the customer. Off: customers are asked to type.'
  },
  {
    key: 'documentReadingEnabled',
    label: 'Read PDF documents (pilot)',
    help:
      'On: PDF documents are read too, up to 2 MB. Off: customers are asked for a photo instead.'
  },
  {
    key: 'paymentSummaryEnabled',
    label: 'Car summary at payment',
    help:
      'On: the payment message starts with a one-line summary of the car and offers "reply change". Off: the old payment message.'
  },
  // One WhatsApp number, several services (spec: assistant-front-door Req 1). All three ship off.
  // Each line says what turning it ON does for a customer, because that is the decision being
  // made here — not what the code does.
  {
    key: 'servicesMenuEnabled',
    group: SERVICES_GROUP,
    label: 'Services menu',
    help:
      'On: the welcome adds an "Other services" button, and the words "menu" or "services" show the list. Off: the welcome is exactly as it is today.'
  },
  {
    key: 'vehicleHistoryEnabled',
    group: SERVICES_GROUP,
    label: 'Vehicle history',
    help:
      'On: customers can order a history report in the chat. Needs at least one report priced and active, or there is nothing to sell them.'
  },
  {
    key: 'trackingEnabled',
    group: SERVICES_GROUP,
    label: 'Shipment tracking',
    help:
      'On: customers can start tracking a shipment in the chat. Off: they are told where tracking happens.'
  },
  // Cars across services (spec 0040_assistant_car_decisions Req 1). Ships ON.
  {
    key: 'carDecisionsEnabled',
    group: SERVICES_GROUP,
    label: 'Cars across services',
    help:
      'On: a customer can say “check the duty for this car” and we confirm which of their cars they mean, read out what they already have, or start the check with the car filled in. Off: they start from scratch. Turning it off doesn’t cut short a question already asked.'
  }
]

// Only the switches are sent: the same settings row also holds the follow-up timings, which this
// page must never overwrite.
export const switchPayload = config =>
  SWITCHES.reduce((out, { key }) => ({ ...out, [key]: Boolean(config?.[key]) }), {})

// A PDF is read by the picture pipeline, so it can only take effect while pictures are on
// (w-admin Req 2.2).
export const documentSwitchDisabled = config => !config?.imageReadingEnabled

// "44% of 312" — every rate shows what it is a rate OF, so a figure over a handful of
// conversations is never mistaken for a trend (w-admin Req 3.3). The percentage is the API's own.
export const percentOf = (pct, denominator) => {
  const total = Number(denominator) || 0
  if (!total) return '—'
  const value = Number(pct) || 0
  const shown = Number.isInteger(value) ? value : value.toFixed(1)
  return `${shown}% of ${total}`
}

export const formatUsd = value => `$${(Number(value) || 0).toFixed(2)}`

// The API counts each way a list can be answered; the report groups them the way the owner asked
// (tapped / typed a number / typed the value / other).
export const groupChoiceMethods = bucket => {
  const b = bucket || {}
  const n = key => Number(b[key]) || 0
  return {
    tap: n('tap'),
    number: n('number') + n('ordinal'),
    value: n('value') + n('token') + n('inferred'),
    other: n('other') + n('ambiguous'),
    total: n('total'),
    typedPct: n('typedPct')
  }
}

export const SLOT_LABELS = {
  engine_size: 'Engine size',
  fuel_type: 'Fuel type',
  trim: 'Trim',
  drive_train: 'Drive train',
  condition: 'Condition',
  body_type: 'Body type',
  origin: 'Buying from',
  car_year: 'Year',
  make: 'Make',
  model: 'Model'
}

export const slotLabel = slot => SLOT_LABELS[slot] || slot

export const DISCARD_LABELS = {
  market: 'talked about markets or a country',
  origin: 'blamed the export country',
  chosen: 'doubted a detail the customer chose'
}

// Where each service's customers came from (spec: assistant-front-door w-admin Req 3.1).
//
// The API counts and computes the percentages; this only puts them in reading order and names the
// signals in words. Duty first and always present, even at zero: a routing report that hid the
// default would hide the one case nearly every conversation falls into, and "vehicle history got
// nine" means nothing without "duty got a thousand" beside it.
export const SIGNAL_LABELS = {
  default: 'No signal (duty)',
  referral: 'Ad click',
  prefill: 'Ad or link text',
  menu: 'Services menu',
  button: 'Button',
  phrase: 'What they typed',
  document: 'A document they sent',
  handoff: 'Handed over'
}

export const signalLabel = key => SIGNAL_LABELS[key] || key || '—'

export const routingRows = (routes, serviceLabel = s => s) => {
  const byService = routes?.byService || {}
  const keys = Object.keys(byService)
  // Duty first, then whatever else exists, biggest first — the order somebody reads it in.
  keys.sort((a, b) => {
    if (a === 'duty') return -1
    if (b === 'duty') return 1
    return (byService[b]?.total || 0) - (byService[a]?.total || 0)
  })
  return keys.map(key => {
    const row = byService[key] || {}
    const signals = Object.entries(row.bySignal || {}).sort((a, b) => b[1] - a[1])
    return {
      key,
      service: serviceLabel(key),
      total: row.total || 0,
      sharePct: row.sharePct,
      signals: signals.map(([signal, count]) => ({
        signal,
        label: signalLabel(signal),
        count,
        pct: row.signalPct?.[signal]
      }))
    }
  })
}

// "3 switched · 1 stayed" — a count map read as a sentence. `stay` is not a service, it is the
// customer declining, and it reads last because it is the absence of the thing being counted.
// Where the markets a history customer picked are named. The keys are the API's own, and an
// unknown one is shown rather than hidden: a market that appears here and not in this table is a
// catalogue the owner has opened and this page has not caught up with.
export const VH_MARKET_LABELS = {
  north_america: 'USA or Canada',
  china: 'China',
  japan: 'Japan',
  korea: 'South Korea',
  europe: 'Europe',
  russia_belarus: 'Russia or Belarus',
  oem: 'Manufacturer records'
}

export const countLine = (counts, labels = {}) => {
  const entries = Object.entries(counts || {})
  if (!entries.length) return 'None'
  return entries
    .sort((a, b) => b[1] - a[1])
    .map(([key, n]) => `${labelFor(labels, key) || key} ${n}`)
    .join(' · ')
}

export const outcomeLine = (counts, serviceLabel = s => s) => {
  const entries = Object.entries(counts || {})
  if (!entries.length) return 'None'
  entries.sort((a, b) => (a[0] === 'stay' ? 1 : b[0] === 'stay' ? -1 : b[1] - a[1]))
  return entries
    .map(([key, n]) => (key === 'stay' ? `${n} stayed` : `${n} → ${serviceLabel(key)}`))
    .join(' · ')
}
